import { Selection, EnterElement, scaleBand, ScaleBand, select, tickStep } from "d3"
import { D3TracesWrapper } from "./D3TracesWrapper"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { ModalityGraphConfig } from "../../../../Types/Graph"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { getSpectrogramFrequencyLabels, RenderStrategy, SpectrogramTraceConfig, SpectrogramTraceConfigJSON } from "../../../../Types/Trace"
import { D3BandAxis } from "./D3BandAxis"
import { D3SpectrogramGraphLegend } from "./D3SpectrogramGraphLegend"
import { D3SpectrogramGridLines } from "./D3SpectrogramGridLines"

type SpectrogramGraphConfig = Omit<ModalityGraphConfig, "traces"> & { traces: SpectrogramTraceConfig[] }

type FrequencyTicks = {
	tickValues: string[]
	formatTick: (value: string) => string
}

const FREQUENCY_TICK_COUNT = 5

export class D3SpectrogramGraph extends D3OneToOneRenderable<SVGGElement, SVGGElement, ModalityGraphConfig, ModalityGraphGroupReactCallbacks> {
	private tracePositionScale: ScaleBand<string> = scaleBand()
	private pageManager: TimeSeriesPageManager<ModalityPage>
	private graph: SpectrogramGraphConfig
	private foregroundClassName: string = "d3-spectrogram-graph-foreground"
	private backgroundClassName: string = "d3-spectrogram-graph-background"

	private foregroundRoot?: SVGGElement
	private backgroundRoot?: SVGGElement
	private axesContainer?: SVGGElement
	private gridLinesContainer?: SVGGElement
	private tracesWrapper?: D3TracesWrapper
	private yAxes: Map<string, D3BandAxis> = new Map()
	private yAxisRoots: Map<string, SVGGElement> = new Map()
	private yGridLines: Map<string, D3SpectrogramGridLines> = new Map()
	private yGridLineRoots: Map<string, SVGGElement> = new Map()
	private legend?: D3SpectrogramGraphLegend

	constructor(root: SVGGElement, config: ModalityGraphConfig, pageManager: TimeSeriesPageManager<ModalityPage>, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, "d3-modality-spectrogram-graph", reactCallbacks)
		this.pageManager = pageManager
		this.graph = this.getDerivedGraph()

		this.updateDerivedState()
		this.render()
	}

	protected updateDerivedState(): void {
		const visibleTraces = this.getVisibleSpectrogramTraces()

		this.tracePositionScale
			.domain(visibleTraces.map(trace => trace.id))
			.range([0, this.config.height])

		if (visibleTraces.length > 3) {
			this.tracePositionScale.paddingInner(0.3)
		} else if (visibleTraces.length > 1) {
			this.tracePositionScale.paddingInner(0.2)
		} else {
			this.tracePositionScale.paddingInner(0)
		}

		this.graph = this.getDerivedGraph()
		this.updateChildren()
	}

	public renderPage = (page: ModalityPage) => {
		this.tracesWrapper?.renderPage(page)
	}

	public viewTimesChanged = () => {
		this.tracesWrapper?.render()
	}

	public rescale = () => {
		this.yAxes.forEach(axis => axis.render())
		this.yGridLines.forEach(gridLines => gridLines.render())
		this.tracesWrapper?.rescale()
	}

	public takeSnapshot = () => {
		this.tracesWrapper?.takeSnapshot()
	}

	public clearSnapshot = () => {
		this.tracesWrapper?.clearSnapshot()
	}

	public isRescaling = (): boolean => {
		return this.tracesWrapper?.isRescaling() ?? false
	}

	public autoScale = () => {}

	private getVisibleSpectrogramTraces(): SpectrogramTraceConfigJSON[] {
		return this.config.traces
			.filter((trace): trace is SpectrogramTraceConfigJSON & { dataObjectId: number } => trace.renderStrategy === RenderStrategy.SPECTROGRAM)
			.filter(trace => this.config.hideEmptyGraphs ? (trace.isVisible ?? true) : true) // Hide spectrograms that are not visible when the graph view is compressed.
	}

	public getDerivedGraph(): SpectrogramGraphConfig {
		return {
			...this.config,
			traces: this.getVisibleSpectrogramTraces().map(traceConfig => {
				const traceYStart = this.tracePositionScale(traceConfig.id) ?? 0
				const traceBandHeight = this.tracePositionScale.bandwidth()
				const frequencyLabels = getSpectrogramFrequencyLabels(traceConfig)

				const frequencyScale: ScaleBand<string> = scaleBand<string>()
					.domain([...frequencyLabels].reverse())
					.range([traceYStart, traceYStart + traceBandHeight])
					.paddingInner(0)
					.paddingOuter(0.5)

				return {
					...traceConfig,
					graphId: this.config.id,
					xScale: this.config.xScale,
					yScale: frequencyScale,
				}
			}),
		}
	}

	protected enter = (newGraph: Selection<EnterElement, any, any, any>): Selection<SVGGElement, any, any, any> => {
		const graphGroup = newGraph
			.append("g")
			.attr("class", this.className)
			.attr("transform", `translate(0, ${this.config.offset})`)

		graphGroup
			.append("g")
			.attr("class", this.backgroundClassName)
			.each(this.createBackgroundChildren)

		graphGroup
			.append("g")
			.attr("class", this.foregroundClassName)
			.each(this.createForegroundChildren)

		return graphGroup
	}

	protected update = (updatedGraphs: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		updatedGraphs.attr("transform", `translate(0, ${this.config.offset})`)
		this.renderChildren()
		return updatedGraphs
	}

	protected createForegroundChildren = (_config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		this.foregroundRoot = nodes[index]

		// Containers are created here, in paint order (axes below the legend),
		// so axes added later can plain-append without re-deriving sibling order.
		this.axesContainer = select(this.foregroundRoot).append<SVGGElement>("g").attr("class", "d3-band-axes").node() ?? undefined

		this.graph.traces.forEach(trace => this.createAxis(trace))

		this.legend = new D3SpectrogramGraphLegend(
			this.foregroundRoot,
			{
				traces: this.graph.traces,
				tracePositionScale: this.tracePositionScale,
				xOffset: this.config.width,
				graphId: this.config.id,
			},
			"d3-spectrogram-graph-legend",
			this.reactCallbacks
		)
	}

	protected createBackgroundChildren = (_config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		this.backgroundRoot = nodes[index]

		// Created before the traces wrapper so grid lines always paint underneath.
		this.gridLinesContainer = select(this.backgroundRoot).append<SVGGElement>("g").attr("class", "d3-spectrogram-grid-lines").node() ?? undefined

		this.graph.traces.forEach(trace => this.createGridLines(trace))

		this.tracesWrapper = new D3TracesWrapper(this.backgroundRoot, { traces: this.graph.traces }, this.pageManager, this.reactCallbacks)
	}

	protected updateChildren = () => {
		this.tracesWrapper?.updateConfig({ traces: this.graph.traces })

		const activeTraceIds = new Set(this.graph.traces.map(trace => trace.id))
		this.yAxes.forEach((_, traceId) => {
			if (!activeTraceIds.has(traceId)) {
				this.removeAxis(traceId)
			}
		})
		this.yGridLines.forEach((_, traceId) => {
			if (!activeTraceIds.has(traceId)) {
				this.removeGridLines(traceId)
			}
		})

		this.graph.traces.forEach(trace => {
			const { tickValues, formatTick } = this.getFrequencyTicks(trace)

			const axis = this.yAxes.get(trace.id)
			if (axis) {
				axis.updateConfig({ scale: trace.yScale, tickValues, formatTick })
			} else {
				this.createAxis(trace)
			}

			const gridLines = this.yGridLines.get(trace.id)
			if (gridLines) {
				gridLines.updateConfig({ scale: trace.yScale, tickValues, width: this.config.width })
			} else {
				this.createGridLines(trace)
			}
		})

		this.legend?.updateConfig({
			traces: this.graph.traces,
			tracePositionScale: this.tracePositionScale,
			xOffset: this.config.width,
			graphId: this.config.id,
		})
	}

	private createAxis = (trace: SpectrogramTraceConfig) => {
		if (!this.axesContainer) {
			return
		}

		const axisRoot = select(this.axesContainer)
			.append<SVGGElement>("g")
			.attr("class", "d3-band-axis-root")
			.node()

		if (!axisRoot) {
			return
		}

		const { tickValues, formatTick } = this.getFrequencyTicks(trace)
		const axis = new D3BandAxis(axisRoot, { scale: trace.yScale, tickValues, formatTick }, this.reactCallbacks)

		this.yAxisRoots.set(trace.id, axisRoot)
		this.yAxes.set(trace.id, axis)
	}

	private removeAxis = (traceId: string) => {
		const axisRoot = this.yAxisRoots.get(traceId)

		if (axisRoot) {
			select(axisRoot).remove()
		}

		this.yAxisRoots.delete(traceId)
		this.yAxes.delete(traceId)
	}

	private createGridLines = (trace: SpectrogramTraceConfig) => {
		if (!this.gridLinesContainer) {
			return
		}

		const gridLinesRoot = select(this.gridLinesContainer).append<SVGGElement>("g").node()

		if (!gridLinesRoot) {
			return
		}

		const gridLines = new D3SpectrogramGridLines(gridLinesRoot, {
			scale: trace.yScale,
			tickValues: this.getFrequencyTicks(trace).tickValues,
			width: this.config.width,
		})

		this.yGridLineRoots.set(trace.id, gridLinesRoot)
		this.yGridLines.set(trace.id, gridLines)
	}

	private removeGridLines = (traceId: string) => {
		const gridLinesRoot = this.yGridLineRoots.get(traceId)

		if (gridLinesRoot) {
			select(gridLinesRoot).remove()
		}

		this.yGridLineRoots.delete(traceId)
		this.yGridLines.delete(traceId)
	}

	private renderChildren = () => {
		this.tracesWrapper?.render()
		this.yAxes.forEach(axis => axis.render())
		this.yGridLines.forEach(gridLines => gridLines.render())
		this.legend?.render()
	}

	// Tick targets are derived from the frequency domain extent (~FREQUENCY_TICK_COUNT nice
	// steps), then snapped to the closest band labels.
	private getFrequencyTicks(trace: SpectrogramTraceConfig): FrequencyTicks {
		const domain = trace.yScale.domain()
		const numericDomain = domain.map(label => Number(label))
		const finiteValues = numericDomain.filter(value => Number.isFinite(value))

		if (finiteValues.length === 0) {
			return { tickValues: [], formatTick: value => value }
		}

		const minValue = Math.min(...finiteValues)
		const maxValue = Math.max(...finiteValues)
		const step = maxValue > minValue ? tickStep(minValue, maxValue, FREQUENCY_TICK_COUNT) : 1

		const tickValues: string[] = []

		for (let target = Math.floor(minValue / step) * step; target <= maxValue + step / 2; target += step) {
			let closestLabel: string | undefined
			let smallestDifference = Number.POSITIVE_INFINITY

			numericDomain.forEach((value, index) => {
				const difference = Math.abs(value - target)
				if (difference < smallestDifference) {
					smallestDifference = difference
					closestLabel = domain[index]
				}
			})

			if (closestLabel && !tickValues.includes(closestLabel)) {
				tickValues.push(closestLabel)
			}
		}

		const formatTick = (value: string): string => {
			const numericValue = Number(value)

			if (Number.isNaN(numericValue)) {
				return value
			}

			return (Math.round(numericValue / step) * step).toString()
		}

		return { tickValues, formatTick }
	}
}
